import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRightIcon, CheckIcon, Loader2Icon, LockIcon } from "lucide-react";
import { FormField, fieldClass } from "./FormField";
import { useEnquiryForm } from "../hooks/useEnquiryForm";
import { serviceOptions } from "../data/footerLinks";
const ease = [0.23, 1, 0.32, 1] as const;
const MAX_MESSAGE = 600;
export function EnquiryForm() {
  const {
    values,
    errors,
    status,
    onChange,
    setField,
    onSubmit,
    reset
  } = useEnquiryForm();
  return <AnimatePresence mode="wait" initial={false} data-id="element-47" data-mp="2k1x2ptel3p2q">
      {status === "success" ? <motion.div key="success" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="flex min-h-[560px] flex-col items-start justify-center" role="status" data-id="element-48" data-mp="261m94ilw3wdv">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-white" data-id="element-49" data-mp="8abc4kg93e5j">
            <CheckIcon className="h-6 w-6" strokeWidth={2} data-id="element-50" data-mp="i10j4zs4vt7x" />
          </span>
          <h3 className="mt-8 text-[36px] font-semibold tracking-[-0.035em] text-ink" data-id="element-51" data-mp="d3rvwks4oyzt">
            Thank you, {values.name.split(" ")[0]}.
          </h3>
          <p className="mt-4 max-w-[440px] text-[17px] leading-[1.6] text-stone" data-id="element-52" data-mp="ru7jg680oj15">
            Your enquiry about <span className="font-semibold text-ink" data-id="element-53" data-mp="mv4oxfpnxv7f">{values.service}</span> has
            been received. A member of our team will be in touch, usually within two working hours.
          </p>
          <button type="button" onClick={reset} className="mt-10 border-b border-ink/30 pb-1 text-[14px] font-semibold text-ink transition-colors duration-200 hover:border-accent" data-id="element-54" data-mp="3qbv1kdfr36i3">
            Send another enquiry
          </button>
        </motion.div> : <motion.form key="form" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onSubmit={onSubmit} noValidate aria-label="Enquiry form" className="space-y-7" data-id="element-55" data-mp="1kimzmw2vx7e2">
          <fieldset data-id="element-56" data-mp="1wjcwakqqs0ed">
            <legend className="mb-3 text-[14px] font-semibold text-ink" data-id="element-57" data-mp="1witjtjjv247q">What can we help with?</legend>
            <div role="radiogroup" className="flex flex-wrap gap-2" aria-describedby={errors.service ? "service-error" : undefined} data-id="element-58" data-mp="21h4w00xmoqt4">
              {serviceOptions.map(opt => {
            const selected = values.service === opt;
            return <button key={opt} type="button" role="radio" aria-checked={selected} onClick={() => setField("service", opt)} className={`inline-flex h-11 items-center gap-2 rounded-full border px-5 text-[14px] font-medium transition-colors duration-150 ${selected ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink/40"}`} data-id="element-59" data-mp="1dslm5gyxk5yx">
                    {selected && <CheckIcon className="h-3.5 w-3.5 text-accent" strokeWidth={2.5} aria-hidden="true" data-id="element-60" data-mp="2uccgjik5ha4z" />}
                    {opt}
                  </button>;
          })}
            </div>
            {errors.service && <p id="service-error" role="alert" className="mt-2 text-[13px] font-medium text-accent" data-id="element-61" data-mp="2kf81e2oq2exo">
                {errors.service}
              </p>}
          </fieldset>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-62" data-mp="2bn65mhpllmtq">
            <FormField id="name" label="Full name" error={errors.name} data-id="element-63" data-mp="26na36mwju3ga">
              <input id="name" name="name" autoComplete="name" placeholder="Jane Smith" value={values.name} onChange={onChange} aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} className={`${fieldClass(!!errors.name)} h-14`} data-id="element-64" data-mp="26kxdtoogoqt7" />
            </FormField>
            <FormField id="email" label="Email address" error={errors.email} data-id="element-65" data-mp="3n225af638bap">
              <input id="email" name="email" type="email" autoComplete="email" placeholder="jane@company.co.uk" value={values.email} onChange={onChange} aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} className={`${fieldClass(!!errors.email)} h-14`} data-id="element-66" data-mp="3s03p52bc8en8" />
            </FormField>
          </div>

          <FormField id="phone" label="Phone number" optional data-id="element-67" data-mp="12ajlcraf6dc4">
            <input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="+44 7700 900000" value={values.phone} onChange={onChange} className={`${fieldClass(false)} h-14`} data-id="element-68" data-mp="128ysd63r72gd" />
          </FormField>

          <FormField id="message" label="Briefly describe your matter" error={errors.message} hint={`${values.message.length}/${MAX_MESSAGE}`} data-id="element-69" data-mp="31ii51iw9uy0h">
            <textarea id="message" name="message" rows={5} maxLength={MAX_MESSAGE} value={values.message} onChange={onChange} placeholder="A few sentences is enough — who's involved, what's happened, and any deadlines." aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined} className={`${fieldClass(!!errors.message)} resize-none py-4 leading-[1.6]`} data-id="element-70" data-mp="3ai54hz5fxtqw" />
          </FormField>

          <div className="flex flex-col gap-5 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between" data-id="element-71" data-mp="1l632e5b4l5es">
            <p className="flex max-w-[300px] items-start gap-2 text-[13px] leading-[1.5] text-stone" data-id="element-72" data-mp="3q4kmgrxeiay1">
              <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-73" data-mp="3u665kv0j3lgo" />
              <span data-id="element-74" data-mp="30wet14pn360">
                Confidential. By submitting, you agree to our{" "}
                <a href="#" className="text-ink underline decoration-line underline-offset-2 hover:decoration-ink" data-id="element-75" data-mp="7zptuxznb28a">
                  privacy policy
                </a>
                .
              </span>
            </p>
            <button type="submit" disabled={status === "submitting"} className="group inline-flex h-14 shrink-0 items-center justify-center gap-4 rounded-full bg-accent pl-7 pr-2 text-[15px] font-semibold text-ink transition-colors duration-150 hover:bg-[#F0572F] disabled:cursor-wait disabled:opacity-80" data-id="element-76" data-mp="hqbzq0hk8eds">
              {status === "submitting" ? "Sending…" : "Send Enquiry"}
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-white" data-id="element-77" data-mp="15ua9tor3u0yc">
                {status === "submitting" ? <Loader2Icon className="h-4 w-4 animate-spin" strokeWidth={2} data-id="element-78" data-mp="vu18xxm63rcm" /> : <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:rotate-45" strokeWidth={2} data-id="element-79" data-mp="rtziylpsomd6" />}
              </span>
            </button>
          </div>
        </motion.form>}
    </AnimatePresence>;
}